import { useState, useRef } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Trash2, Loader2, FileUp } from "lucide-react";
import { toast } from "sonner";

const CATS = ["Pharmacology", "Pharmaceutics", "Medicinal Chemistry", "Pharmacognosy", "Clinical Pharmacy", "GPAT/NIPER Prep"];
const uid = () => Math.random().toString(36).slice(2);

export function CourseFormModal({ open, onClose, initial, onSaved }) {
  const empty = { title: "", description: "", category: "Pharmacology", is_free: true, price: 0, cover_image: "", level: "Beginner", instructor: "EZ Education", published: true, modules: [] };
  const [form, setForm] = useState(initial ? JSON.parse(JSON.stringify(initial)) : empty);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addModule = () => set("modules", [...form.modules, { id: uid(), title: "", lessons: [] }]);
  const updModule = (mi, k, v) => { const m = [...form.modules]; m[mi][k] = v; set("modules", m); };
  const delModule = (mi) => set("modules", form.modules.filter((_, i) => i !== mi));
  const addLesson = (mi) => { const m = [...form.modules]; m[mi].lessons.push({ id: uid(), title: "", video_url: "", pdf_path: "", pdf_name: "", duration: "" }); set("modules", m); };
  const updLesson = (mi, li, k, v) => { const m = [...form.modules]; m[mi].lessons[li][k] = v; set("modules", m); };
  const delLesson = (mi, li) => { const m = [...form.modules]; m[mi].lessons = m[mi].lessons.filter((_, i) => i !== li); set("modules", m); };

  const uploadPdf = async (mi, li, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updLesson(mi, li, "pdf_path", data.path);
      updLesson(mi, li, "pdf_name", data.filename);
      toast.success("PDF uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    setSaving(true);
    const payload = { ...form, price: form.is_free ? 0 : Number(form.price) };
    try {
      if (initial) await api.put(`/admin/courses/${initial.id}`, payload);
      else await api.post("/admin/courses", payload);
      toast.success("Course saved");
      onSaved();
      onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>{initial ? "Edit Course" : "Add Course"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Title</Label><Input data-testid="course-form-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5" rows={3} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Level</Label>
              <Select value={form.level} onValueChange={(v) => set("level", v)}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{["Beginner", "Intermediate", "Advanced"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Cover Image URL</Label><Input value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} className="mt-1.5" placeholder="https://..." /></div>
          <div><Label>Instructor</Label><Input value={form.instructor} onChange={(e) => set("instructor", e.target.value)} className="mt-1.5" /></div>

          <div className="flex items-center gap-6 py-2">
            <div className="flex items-center gap-2"><Switch data-testid="course-form-free-switch" checked={form.is_free} onCheckedChange={(v) => set("is_free", v)} /><Label>Free course</Label></div>
            {!form.is_free && <div className="flex items-center gap-2"><Label>Price ₹</Label><Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} className="w-28" /></div>}
          </div>

          {/* Modules */}
          <div className="border-t border-slate-200 pt-4">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-base">Modules & Lessons</Label>
              <Button type="button" size="sm" variant="outline" onClick={addModule}><Plus className="h-4 w-4 mr-1" /> Module</Button>
            </div>
            <div className="space-y-4">
              {form.modules.map((m, mi) => (
                <div key={m.id} className="rounded-xl border border-slate-200 p-4 bg-slate-50">
                  <div className="flex items-center gap-2 mb-3">
                    <Input value={m.title} onChange={(e) => updModule(mi, "title", e.target.value)} placeholder={`Module ${mi + 1} title`} className="bg-white" />
                    <Button type="button" size="icon" variant="ghost" onClick={() => delModule(mi)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                  </div>
                  <div className="space-y-3">
                    {m.lessons.map((l, li) => (
                      <div key={l.id} className="rounded-lg border border-slate-200 p-3 bg-white space-y-2">
                        <div className="flex items-center gap-2">
                          <Input value={l.title} onChange={(e) => updLesson(mi, li, "title", e.target.value)} placeholder="Lesson title" />
                          <Input value={l.duration} onChange={(e) => updLesson(mi, li, "duration", e.target.value)} placeholder="12:30" className="w-24" />
                          <Button type="button" size="icon" variant="ghost" onClick={() => delLesson(mi, li)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                        </div>
                        <Input value={l.video_url} onChange={(e) => updLesson(mi, li, "video_url", e.target.value)} placeholder="Paste any YouTube / Vimeo / Google Drive link" />
                        <div className="flex items-center gap-2">
                          <PdfUpload onFile={(f) => uploadPdf(mi, li, f)} />
                          <span className="text-xs text-slate-500 truncate">{l.pdf_name || "No PDF"}</span>
                        </div>
                      </div>
                    ))}
                    <Button type="button" size="sm" variant="outline" onClick={() => addLesson(mi)} className="w-full"><Plus className="h-4 w-4 mr-1" /> Lesson</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Button data-testid="course-form-save" onClick={save} disabled={saving} className="w-full bg-teal-700 hover:bg-teal-600 text-white h-11">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Course"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PdfUpload({ onFile }) {
  const ref = useRef();
  return (
    <>
      <input ref={ref} type="file" accept="application/pdf" className="hidden" onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
      <Button type="button" size="sm" variant="outline" onClick={() => ref.current?.click()}><FileUp className="h-4 w-4 mr-1" /> PDF</Button>
    </>
  );
}
